import React, { useState, useMemo, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";

import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Row,
} from "reactstrap";

import CommonTable from "../../../components/Common/TableContainer";
import { submitAIScheduleMatrixGroupFilterData } from "model/AIScheduleMatrixGroupModel";
import AIScheduleMatrixGroupAddEditModal from "pages/AllCommonModal/AIScheduleMatrixGroupAddEditModal/ai-schedule-matrix-group-add-edit-modal";
import Loaders from "components/Common/Loaders";
import { usePermissionContext } from "libs/context/PermissionContext";
import { useAIScheduleMatrixGroupContext } from "libs/context/AIScheduleMatrixGroupContext";
import { DEFAULT_PAGE_SIZE } from "helpers/GeneralHelper";
import { useCommonTableSettingsPageSize } from "hooks/useSettingsReadyTablePageSize";

const AIScheduleMatrixGroup = () => {
  const { permissionData } = usePermissionContext() || {};
  const {
    fetchAIScheduleMatrixGroupContextData,
    aiScheduleMatrixGroupContextData,
  } = useAIScheduleMatrixGroupContext() || {};
  const { pageSize: settingsPageSize, settingsReady } =
    useCommonTableSettingsPageSize("AI Schedule Matrix Group");
  const initialFilterFetchedRef = useRef(false);

  const [aiScheduleMatrixGroupData, setAIScheduleMatrixGroupData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [tableMetaData, setTableMetadata] = useState();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editItemId, setEditItemId] = useState(null);
  const [requestPayload, setRequestPayload] = useState({
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [paginationMeta, setPaginationMeta] = useState({
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    totalPages: 0,
    totalRecords: 0,
  });

  const filteredData = useMemo(
    () => aiScheduleMatrixGroupData,
    [aiScheduleMatrixGroupData]
  );

  const actionPermissions = useMemo(
    () => ({
      canCreate: Boolean(permissionData?.AIScheduleMatrixGroupAddAccess),
      canEdit: Boolean(permissionData?.AIScheduleMatrixGroupUpdateAccess),
    }),
    [permissionData]
  );

  const hasAnyRowAction = actionPermissions.canEdit;

  const getAIScheduleMatrixGroupList = async (payloadOverrides = {}) => {
    setLoading(true);
    const payload = {
      ...requestPayload,
      ...payloadOverrides,
    };
    const response = await submitAIScheduleMatrixGroupFilterData(payload);
    const responseData = response?.data || {};
    const listData =
      responseData?.records ?? (Array.isArray(responseData) ? responseData : []);
    const valueReModified = (Array.isArray(listData) ? listData : []).map((item) => ({
      ...item,
      ID: item?.id,
    }));

    if (response) {
      setAIScheduleMatrixGroupData(valueReModified);
      setPaginationMeta({
        pageNumber:
          Number(responseData?.pageNumber) || Number(payload?.pageNumber) || 1,
        pageSize:
          Number(responseData?.pageSize) ||
          Number(payload?.pageSize) ||
          DEFAULT_PAGE_SIZE,
        totalPages: Number(responseData?.totalPages) || 0,
        totalRecords:
          Number(responseData?.totalRecords) || valueReModified.length,
      });
      setRequestPayload(payload);
    } else {
      setAIScheduleMatrixGroupData([]);
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchAIScheduleMatrixGroupContextData?.();
  }, []);

  useEffect(() => {
    if (!settingsReady || initialFilterFetchedRef.current) return;
    initialFilterFetchedRef.current = true;
    const pageSize = Number(settingsPageSize) || DEFAULT_PAGE_SIZE;
    setRequestPayload((previous) => ({ ...previous, pageSize }));
    setPaginationMeta((previous) => ({ ...previous, pageSize }));
    getAIScheduleMatrixGroupList({ pageNumber: 1, pageSize });
  }, [settingsReady, settingsPageSize]);

  useEffect(() => {
    const valueReModified = aiScheduleMatrixGroupContextData?.table?.columns?.map(
      (col) => ({
        Header: col?.title,
        accessor: col?.field,
        type: col?.type,
      })
    );
    setTableMetadata(valueReModified);
  }, [aiScheduleMatrixGroupContextData]);

  const handleEdit = (row) => {
    setEditItemId(row?.id ?? null);
    setEditModalOpen(true);
  };

  const handleCreate = () => {
    setEditItemId(null);
    setEditModalOpen(true);
  };

  const handleCloseModal = () => {
    setEditModalOpen(false);
    setEditItemId(null);
  };

  const handleSearchSubmit = async (value) => {
    const trimmedValue = value?.trim() || "";
    await getAIScheduleMatrixGroupList({
      ...requestPayload,
      pageNumber: 1,
      search: trimmedValue,
    });
  };

  const handlePageChange = async (page) => {
    await getAIScheduleMatrixGroupList({ pageNumber: page });
  };

  const handleRowsPerPageChange = async (rows) => {
    await getAIScheduleMatrixGroupList({ pageNumber: 1, pageSize: rows });
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Card>
            <CardBody>
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="fw-bold mb-3">AI Schedule Matrix Group</h5>

                {actionPermissions.canCreate ? (
                  <Col xs="12" md="4" className="text-md-end mt-2 mt-md-0">
                    <Button color="primary" onClick={handleCreate}>
                      + Create
                    </Button>
                  </Col>
                ) : null}
              </div>
              <hr />

              <Row className="align-items-center g-2 mb-4">
                <Col xs="12" md="8">
                  <div className="d-flex border rounded w-100 category-search-input">
                    <Input
                      type="text"
                      placeholder="Search by title"
                      className="border-0"
                      value={searchQuery}
                      onChange={(e) => {
                        const nextValue = e?.target?.value || "";
                        setSearchQuery(nextValue);
                        if (!nextValue.trim()) {
                          handleSearchSubmit("");
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleSearchSubmit(searchQuery?.trim());
                        }
                      }}
                    />
                    <Button
                      className="search-icon-button"
                      onClick={() => handleSearchSubmit(searchQuery?.trim())}
                    >
                      <i className="fa fa-search"></i>
                    </Button>
                  </div>
                </Col>
              </Row>

              {!loading && tableMetaData?.length ? (
                <CommonTable
                  columns={tableMetaData}
                  data={filteredData}
                  isActionRequired={hasAnyRowAction}
                  onEdit={actionPermissions.canEdit ? handleEdit : undefined}
                  isEditAccess={actionPermissions.canEdit}
                  isCheckboxLeftSide={false}
                  isPagination={true}
                  currentPage={paginationMeta?.pageNumber}
                  rowsPerPage={paginationMeta?.pageSize}
                  totalPages={paginationMeta?.totalPages}
                  totalRecords={paginationMeta?.totalRecords}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handleRowsPerPageChange}
                  pageName="AI Schedule Matrix Group"
                />
              ) : (
                <Loaders />
              )}
            </CardBody>
          </Card>

          <AIScheduleMatrixGroupAddEditModal
            isOpen={editModalOpen}
            onClose={handleCloseModal}
            itemId={editItemId}
            onSuccess={getAIScheduleMatrixGroupList}
          />
        </Container>
      </div>
    </React.Fragment>
  );
};

AIScheduleMatrixGroup.propTypes = {
  orders: PropTypes.array,
  onGetOrders: PropTypes.func,
};

export default withRouter(AIScheduleMatrixGroup);
